import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

export default function SEOTab() {
  const { settings, setSettings, refresh } = useData();
  const { toast } = useToast();
  const [local, setLocal] = useState(settings);
  useEffect(() => setLocal(settings), [settings]);
  const seo = local.seo || {};

  const persistSEO = async (patch) => {
    const next = { ...seo, ...patch };
    setLocal({ ...local, seo: next });
    setSettings({ ...local, seo: next });
    try {
      await api.put('/api/settings', { seo: next });
      await refresh();
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };
  const updateLocal = (patch) => setLocal({ ...local, seo: { ...seo, ...patch } });

  const testVerification = () => {
    const dom = window.location.origin;
    window.open(`https://search.google.com/search-console?resource_id=${encodeURIComponent(dom)}`, '_blank');
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-bold text-nv-wine flex items-center gap-2">
          <span className="inline-flex w-8 h-8 rounded-lg bg-orange-100 text-orange-700 items-center justify-center text-sm">🔍</span>
          Search Engine Verification
        </h3>
        <p className="text-sm text-nv-muted">Paste the verification content code (only the <code>content=""</code> value) provided by each search engine.</p>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Google Search Console</label>
          <input value={seo.googleVerification || ''} onChange={(e) => updateLocal({ googleVerification: e.target.value.replace(/^"|"$/g, '') })} onBlur={(e) => persistSEO({ googleVerification: e.target.value.replace(/^"|"$/g, '') })} placeholder="ABCDEfghij1234... (from HTML tag method)" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Bing Webmaster Tools</label>
          <input value={seo.bingVerification || ''} onChange={(e) => updateLocal({ bingVerification: e.target.value.replace(/^"|"$/g, '') })} onBlur={(e) => persistSEO({ bingVerification: e.target.value.replace(/^"|"$/g, '') })} placeholder="msvalidate.01 content value" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Pinterest</label>
          <input value={seo.pinterestVerification || ''} onChange={(e) => updateLocal({ pinterestVerification: e.target.value.replace(/^"|"$/g, '') })} onBlur={(e) => persistSEO({ pinterestVerification: e.target.value.replace(/^"|"$/g, '') })} placeholder="p:domain_verify content value" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
        </div>

        <button onClick={testVerification} className="text-xs px-3 py-1.5 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-50">
          Open Google Search Console →
        </button>
      </div>

      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-bold text-nv-wine flex items-center gap-2">
          <span className="inline-flex w-8 h-8 rounded-lg bg-blue-100 text-blue-700 items-center justify-center text-sm">📊</span>
          Analytics & Tracking
        </h3>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Google Analytics 4 (Measurement ID)</label>
          <input value={seo.ga4Id || ''} onChange={(e) => updateLocal({ ga4Id: e.target.value.trim() })} onBlur={(e) => persistSEO({ ga4Id: e.target.value.trim() })} placeholder="G-XXXXXXXXXX" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Google Tag Manager (Container ID)</label>
          <input value={seo.gtmId || ''} onChange={(e) => updateLocal({ gtmId: e.target.value.trim() })} onBlur={(e) => persistSEO({ gtmId: e.target.value.trim() })} placeholder="GTM-XXXXXXX" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Facebook Pixel ID</label>
          <input value={seo.fbPixelId || ''} onChange={(e) => updateLocal({ fbPixelId: e.target.value.trim() })} onBlur={(e) => persistSEO({ fbPixelId: e.target.value.trim() })} placeholder="1234567890" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono" />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-800">
          ⚡ Tracking scripts load only on public pages. Admin & dashboard routes are excluded and marked <code>noindex</code>.
        </div>
      </div>

      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 space-y-4 lg:col-span-2">
        <h3 className="text-lg font-bold text-nv-wine flex items-center gap-2">
          <span className="inline-flex w-8 h-8 rounded-lg bg-green-100 text-green-700 items-center justify-center text-sm">📝</span>
          Homepage Meta Overrides <span className="text-xs font-normal text-stone-500">(optional)</span>
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Site Title Override (Homepage)</label>
            <input value={seo.siteTitleOverride || ''} onChange={(e) => updateLocal({ siteTitleOverride: e.target.value })} onBlur={(e) => persistSEO({ siteTitleOverride: e.target.value })} maxLength={70} placeholder="Navavidha Bhakti — Online Puja at Baba Baidyanath Dham" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
            <p className="text-[10px] text-stone-500 mt-1">Max 70 characters for Google.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">OG Share Image URL</label>
            <input value={seo.ogImage || ''} onChange={(e) => updateLocal({ ogImage: e.target.value })} onBlur={(e) => persistSEO({ ogImage: e.target.value })} placeholder="https://navavidhabhakti.com/brand/og.png (1200x630)" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Meta Description Override</label>
            <textarea value={seo.siteDescriptionOverride || ''} onChange={(e) => updateLocal({ siteDescriptionOverride: e.target.value })} onBlur={(e) => persistSEO({ siteDescriptionOverride: e.target.value })} maxLength={160} rows="2" placeholder="Book online pujas at Baba Baidyanath Dham (Deoghar)..." className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
            <p className="text-[10px] text-stone-500 mt-1">Max 160 characters for Google. Current: {(seo.siteDescriptionOverride || '').length}/160.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Focus Keywords (comma separated)</label>
            <input value={seo.keywords || ''} onChange={(e) => updateLocal({ keywords: e.target.value })} onBlur={(e) => persistSEO({ keywords: e.target.value })} placeholder="online puja, baba baidyanath dham, deoghar puja, sankalp" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 space-y-1">
          <p><b>✓ Automatic SEO features already active:</b></p>
          <ul className="list-disc list-inside space-y-0.5 text-[11px]">
            <li>Per-page dynamic <code>&lt;title&gt;</code>, meta description, canonical URL</li>
            <li>Open Graph &amp; Twitter Card tags for rich social sharing</li>
            <li>JSON-LD structured data (Organization + LocalBusiness + WebSite) on homepage</li>
            <li><code>robots.txt</code> + <code>sitemap.xml</code> served at site root</li>
            <li>Admin / dashboard routes auto <code>noindex</code></li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          🔗 <b>Sitemap:</b> <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="underline">/sitemap.xml</a> &nbsp;·&nbsp;
          <b>Robots:</b> <a href="/robots.txt" target="_blank" rel="noreferrer" className="underline">/robots.txt</a>
        </div>
      </div>
    </div>
  );
}
